/**
 * The run's UPPER BAND — the vertical slot its wall cabinets hang in.
 *
 * `run.upperSpec` says where. ONE choice positions them (user ruling
 * 2026-09-19 — "clamp to the ceiling OR clamp to a specific height above the
 * counter"; claude-docs/2026-09-19-upper-cabinet-tier-design.md §3.5):
 *
 *   anchor  `'ceiling'` — the uppers' TOPS are at the ceiling and they hang
 *           down from it. Absent — their BOTTOMS sit `gap` over the
 *           countertop.
 *   gap     metres from the countertop's surface to the uppers' bottoms.
 *           Absent = the standard 18"; 22" is the usual taller choice. Says
 *           nothing under `anchor: 'ceiling'`: it is one clamp or the other.
 *   height  the cabinet's own height, under either anchor. Absent = the
 *           standard 30" (36" and 42" are the other stock wall cabinets).
 *
 * ONE reader, because everything that lines up with the uppers has to agree
 * about the line: the straight uppers, the corner upper L, a tall span ("a
 * tall unit rises to the upper-cabinet line"), the fridge column and its
 * over-fridge cabinet. It was three constants and a sum, re-derived per file.
 *
 * Two limits, both on the HEIGHT rather than the position the user chose:
 *  - counter-set, a cabinet never goes through the ceiling — it is cut short;
 *  - ceiling-hung, a cabinet never comes closer to the counter than the
 *    standard gap — a 42" upper under a 7' ceiling would bottom out 6" over
 *    the counter, so it is cut short at 18" instead.
 *
 * Cut short to a height somebody MAKES (user ruling 2026-10-21, plan C6 —
 * "you cant build a 40" if the prod family doesnt offer it"). A family with a
 * size catalog snaps each box to the tallest height its line makes at that
 * width (`upperTier.buildUpperBox`); a family with none offers the stock
 * heights, so its band is snapped here, to the tallest stock height that fits.
 * Either way the box hangs from the cut line — the top stays on the ceiling
 * and the bottoms rise — so the tall units, the fridge column and the hood,
 * which all read `topY`, are untouched by the snap.
 *
 * The band also says how much the ceiling leaves (`room`: from the lowest the
 * bottoms may sit to the ceiling; null with no ceiling) and what the run asked
 * for (`requested`), so every surface that greys a height the ceiling has no
 * room for (`upperHeights.js`) asks the same number the fill used.
 *
 * With no ceiling known — a caller that did not say, which in the app is none
 * (`materializeRun` always resolves one) — there is nothing to hang from, and
 * the band stays counter-set rather than invent a ceiling.
 *
 * A STACK (plan 2026-10-21, C17): `upperSpec.stack` asks for a second row of
 * uppers standing on the band's top line — `height` in metres, or
 * `'ceiling'` to run to it. The band says where that row stands (`stack`),
 * and `columnTopY` is the line the tall units and the fridge column rise to:
 * the stack's top where there is one, the uppers' otherwise.
 *
 * The COUNTER every measurement starts from is the run's own, read off its
 * dimension profile (`profiles.js`). This is the one place that conversion
 * happens, for the same reason the band has one reader at all: a vanity run
 * with uppers switched on must hang them over its 32" counter, and a second
 * copy of "counter = cabinet height + thickness" would be a second answer.
 *
 * Pure.
 */

const {
  UPPER_CABINET_DIMENSIONS,
  UPPER_CABINET_GAP_ABOVE_COUNTER,
} = require('../kitchen-assembly/constants');
const { profileOf, counterTopOffsetFor } = require('./profiles');
const { familyOf } = require('./families');
const { familySizes } = require('./vendorSizes');
const {
  STOCK_UPPER_HEIGHTS_IN,
  CEILING_SLACK_IN,
  fitsUnderCeiling,
  offeredUpperHeightIn,
  roomInches,
} = require('./upperHeights');
const { runAliases } = require('./runIds');
const { isPlainFillUpper } = require('../kitchen-assembly/buildUpperCabinet');

const IN = 0.0254;
/** Two lengths the user would call the same (a sixteenth of a millimetre). */
const SAME_LENGTH = 1e-4;

/**
 * The band, when the ceiling cuts it, hung from the cut line at a height
 * somebody makes. Within 1" of the height asked for (`fitsUnderCeiling` — a
 * scanned ceiling is read to the inch, as C8 reads a scan's uppers) it is that
 * full height with its bottom dropped by the shortfall: an 8' ceiling the scan
 * reads at 95.9" still takes 42" uppers. Past that, a family with no size
 * catalog takes the tallest stock height that fits (the same inch of slack);
 * a catalog family's boxes snap per width instead, so its band is only cut.
 * With no stock height that fits at all (under a foot of room) the cut stands
 * — there is nothing shorter to build, and dropping the uppers would be worse
 * than a box no one sells. A band the ceiling does not cut is returned as it
 * is.
 */
function cutToLine(run, band) {
  if (band.height >= band.requested - SAME_LENGTH / 100) return band;
  const roomIn = roomInches(band.room);
  const hung = (height) => ({ ...band, bottomY: band.topY - height, height });
  if (fitsUnderCeiling(roomIn, band.requested / IN))
    return hung(band.requested);
  if (familySizes(familyOf(run))) return band;
  const fits = STOCK_UPPER_HEIGHTS_IN.filter((h) =>
    fitsUnderCeiling(roomIn, h)
  );
  if (!fits.length) return band;
  return hung(Math.max(...fits) * IN);
}

/**
 * A stack as a spec states it — `{ height, glass? }`, `height` a positive
 * length or `'ceiling'` — or null when it says nothing a stack can be built
 * from.
 */
function statedStack(stack) {
  if (!stack || typeof stack !== 'object') return null;
  const { height } = stack;
  if (height !== 'ceiling' && !(typeof height === 'number' && height > 0))
    return null;
  return { height, ...(stack.glass === true ? { glass: true } : {}) };
}

/**
 * Where the stacked row stands over `band`, or null. A sized stack (12",
 * 15") is built only where it fits WHOLE under the ceiling; "To ceiling"
 * needs a ceiling to reach, and none is left to fill where the uppers
 * already reach it — within the inch their own fit allows
 * (`fitsUnderCeiling`).
 *
 * @returns {{ bottomY: number, topY: number, toCeiling: boolean,
 *   glass: boolean }|null} world Y
 */
function stackOver(run, band, ceilingY) {
  const stated = statedStack(run?.upperSpec?.stack);
  if (!stated) return null;
  if (stackRefusal(band.topY, ceilingY, stated.height)) return null;
  const toCeiling = stated.height === 'ceiling';
  return {
    bottomY: band.topY,
    topY: toCeiling
      ? stackCeilingTopY(run, band.topY, ceilingY)
      : band.topY + stated.height,
    toCeiling,
    glass: stated.glass === true,
  };
}

/**
 * Where a "To ceiling" row's top is: only heights the family OFFERS are
 * built (user ruling 2026-10-21, `upperHeights.offeredUpperHeightIn`). A
 * family with no size catalog offers the same stock heights at every width,
 * so the row is the tallest of them that fits — the band's top, which the
 * tall units and the fridge column meet. A catalog family's heights differ by
 * width, so its row runs to the ceiling here and each box is the tallest its
 * line makes under it (`upperStack`) — as the uppers' own band is cut
 * (`cutToLine`) and their boxes snapped per width.
 */
function stackCeilingTopY(run, bottomY, ceilingY) {
  const family = familyOf(run);
  if (familySizes(family)) return ceilingY;
  const heightIn = offeredUpperHeightIn(
    family,
    'wall',
    null,
    (ceilingY - bottomY) / IN
  );
  return heightIn == null ? ceilingY : bottomY + heightIn * IN;
}

const REACHES_CEILING = 'The uppers already reach the ceiling';
const NO_CEILING = 'No ceiling to reach';

/**
 * The shortest stacked cabinet: the shortest stock wall cabinet. "To ceiling"
 * fills the gap exactly in a family with no size catalog, and a gap under
 * this is no cabinet at all — a 6" box with doors over 36" uppers in an 8'
 * room (review finding 2026-10-21). A catalog family makes nothing shorter.
 */
const MIN_STACK_IN = Math.min(...STOCK_UPPER_HEIGHTS_IN);

/**
 * Why a stack `height` (a length or `'ceiling'`) cannot stand on uppers whose
 * top is `topY` under `ceilingY`, or null — the one rule `stackOver` builds
 * by and the panel greys its choices by. The room is said to the half inch
 * below it, as `upperHeights.ceilingReason` says a height's.
 */
function stackRefusal(topY, ceilingY, height) {
  if (ceilingY == null) return height === 'ceiling' ? NO_CEILING : null;
  const above = ceilingY - topY;
  if (above <= CEILING_SLACK_IN * IN + SAME_LENGTH) return REACHES_CEILING;
  const needs = height === 'ceiling' ? MIN_STACK_IN * IN : height;
  if (topY + needs > ceilingY + SAME_LENGTH) {
    const roomIn = Math.floor(roomInches(above) * 2 + 1e-9) / 2;
    return `Only ${+roomIn.toFixed(2)}" above the uppers here`;
  }
  return null;
}

/** `stackRefusal` asked of a band (`upperBandFor`). */
const stackUnavailableReason = (band, height) =>
  stackRefusal(band.topY, band.ceilingY ?? null, height);

/** The line a tall unit and the fridge column rise to: the stack's top
 * where the run has one, else the uppers'. */
const columnTopY = (band) => band.stack?.topY ?? band.topY;

/**
 * Where a wall hood's CHIMNEY cover rises to under a stacked row (user ruling
 * 2026-10-21): the row's top, or the ceiling for "To ceiling", so the run
 * reads as one top line — the hood itself keeps its height, a canopy up to
 * the uppers' line. Null without a stack: the plain canopy, as it always was.
 */
const hoodChimneyTopY = (band) =>
  band?.stack ? (band.stack.toCeiling ? band.ceilingY : band.stack.topY) : null;

/**
 * @param {Object} run   reads `upperSpec`, its profile and its family
 * @param {Object} ctx   `{ floorY, ceilingY? }`
 * @returns {{ bottomY: number, topY: number, height: number,
 *   room: number|null, requested: number, ceilingY: number|null,
 *   stack: Object|null }} world Y; `room` and `requested` are lengths;
 *   `stack` per `stackOver`
 */
function upperBandFor(run, ctx) {
  const band = uppersBandFor(run, ctx);
  const ceilingY = typeof ctx?.ceilingY === 'number' ? ctx.ceilingY : null;
  return {
    ...band,
    ceilingY,
    stack: stackOver(run, band, ceilingY),
  };
}

/** The band the uppers themselves hang in (`upperBandFor`, less the stack). */
function uppersBandFor(run, ctx) {
  const spec = run?.upperSpec || {};
  const floorY = ctx?.floorY ?? 0;
  const ceilingY = ctx?.ceilingY;
  const hasCeiling = typeof ceilingY === 'number';
  // The counter the band is measured from is the RUN's, via its dimension
  // profile — a vanity run whose uppers are switched on hangs them over ITS
  // 32" counter, not the kitchen's 36" line. `profileOf` resolves unset to
  // kitchen, so this is the same two constants for every run that has always
  // existed. The GAP is not profile-scoped: 18" over the counter is the rule
  // this module exists to own (see the header), and it does not change with
  // the counter's height.
  const profile = profileOf(run);
  const counterTopY = floorY + counterTopOffsetFor(run);
  const requested = spec.height ?? UPPER_CABINET_DIMENSIONS.height;

  if (spec.anchor === 'ceiling' && hasCeiling) {
    const lowest = counterTopY + UPPER_CABINET_GAP_ABOVE_COUNTER;
    const bottomY = Math.max(ceilingY - requested, lowest);
    return cutToLine(run, {
      bottomY,
      topY: ceilingY,
      height: ceilingY - bottomY,
      room: ceilingY - lowest,
      requested,
    });
  }

  const bottomY = counterTopY + (spec.gap ?? UPPER_CABINET_GAP_ABOVE_COUNTER);
  if (!hasCeiling) {
    return {
      bottomY,
      topY: bottomY + requested,
      height: requested,
      room: null,
      requested,
    };
  }
  const topY = Math.min(bottomY + requested, ceilingY);
  return cutToLine(run, {
    bottomY,
    topY,
    height: topY - bottomY,
    room: ceilingY - bottomY,
    requested,
  });
}

/**
 * A spec reduced to what it SAYS — the four fields the band and the packer
 * read, and glass uppers, in their stored vocabulary, with everything that
 * says "the standard" left out. Null when that leaves nothing.
 */
function statedUpperSpec(spec) {
  if (!spec || typeof spec !== 'object') return null;
  const out = {};
  if (spec.anchor === 'ceiling') out.anchor = 'ceiling';
  // One clamp or the other: a gap says nothing under a ceiling anchor.
  else if (spec.gap > 0) out.gap = spec.gap;
  if (spec.height > 0) out.height = spec.height;
  if (spec.align === 'independent') out.align = 'independent';
  // Glass uppers (`glassUppers.js`): a fact about the kitchen like the rest,
  // so a new layout keeps them.
  if (spec.glass === true) out.glass = true;
  // The stacked row (C23): kept when it is one the band can build from.
  const stack = statedStack(spec.stack);
  if (stack) out.stack = stack;
  return Object.keys(out).length ? out : null;
}

function sameUpperSpec(a, b) {
  const near = (x, y) =>
    x == null || y == null
      ? x == null && y == null
      : Math.abs(x - y) < SAME_LENGTH;
  return (
    a.anchor === b.anchor &&
    a.align === b.align &&
    a.glass === b.glass &&
    near(a.gap, b.gap) &&
    near(a.height, b.height) &&
    sameStack(a.stack, b.stack, near)
  );
}

function sameStack(a, b, near) {
  if (!a || !b) return !a && !b;
  const sameHeight =
    a.height === 'ceiling' || b.height === 'ceiling'
      ? a.height === b.height
      : near(a.height, b.height);
  return sameHeight && a.glass === b.glass;
}

/**
 * Where a ROOM hangs its uppers — the one spec its runs agree on, or null.
 *
 * The setting is stored per run, but it is a fact about the kitchen: the
 * ceiling it hangs from and the counter it clears are the room's. So a NEW
 * layout for the same room should hang its uppers where the user already put
 * them, and this is what the generator reads to do that
 * (`materializeCandidate` → `layout._upperSpec` → the derived runs). Without
 * it every Generate silently put a 42" ceiling-hung kitchen back to 30" at
 * 18", and the first edit of the new run kept it there.
 *
 * Only runs that STATE something vote: a run nobody touched has no opinion,
 * it is not a vote for the standard. Two runs that state different things
 * have no shared answer, and none is invented — the layout gets the standard.
 * Free-standing legs and runs with their uppers off hang nothing and are
 * skipped.
 *
 * @param {Object[]} runs `room.cabinetRuns`
 * @returns {Object|null} a fresh `upperSpec`, or null
 */
function roomUpperSpec(runs) {
  let shared = null;
  for (const run of runs || []) {
    if (!run || run.uppers === false) continue;
    if (!(run.segments || []).some((seg) => seg && !seg.freestanding)) continue;
    const stated = statedUpperSpec(run.upperSpec);
    if (!stated) continue;
    // The FIRST statement stands; later runs only have to agree with it.
    if (!shared) shared = stated;
    else if (!sameUpperSpec(shared, stated)) return null;
  }
  return shared;
}

/** The chips a measured value snaps to when it is within 1" of one. */
const SCANNED_HEIGHT_CHIPS_IN = [30, 36, 42];
const SCANNED_GAP_CHIPS_IN = [18, 20, 22];
/** How close (inches) a measurement must be to a chip, or a top to the ceiling. */
const SCANNED_SNAP_IN = 1;
/** An over-fridge cabinet is a 24"-deep bridge — the resolver's own test. */
const BRIDGE_DEPTH_IN = 20;

const median = (values) => {
  const v = [...values].sort((a, b) => a - b);
  const mid = Math.floor(v.length / 2);
  return v.length % 2 ? v[mid] : (v[mid - 1] + v[mid]) / 2;
};

/** A measured value as a spec stores it: the chip within 1", else to ½". */
function asChipOrHalfInch(inches, chips) {
  const chip = chips.find((c) => Math.abs(c - inches) <= SCANNED_SNAP_IN);
  return (chip ?? Math.round(inches * 2) / 2) * IN;
}

/**
 * Where a SCANNED run's measured uppers hang, as an `upperSpec` — so that
 * "Rebuilt" builds the kitchen that is there instead of the standard (plan
 * 2026-10-21, C8). Read off the run's own MEASURED uppers: its `upper-cabinet`s
 * that the plain fill did not build, less an over-fridge cabinet (a 24"-deep
 * bridge at its own height). Medians, each snapped to the chip within 1" of
 * it (height 30 / 36 / 42", gap 18 / 20 / 22"), else kept to the half inch; a
 * top within 1" of the ceiling hangs them from it. What equals the standard
 * is left out — the standard is the absence of a setting — and null when
 * nothing is left, or there is nothing measured to read.
 *
 * @param {Object} run       the stored run (its id, aliases and profile)
 * @param {Object[]} objects the room's objects
 * @param {{ floorY: number, ceilingY?: number }} ctx
 * @returns {Object|null} a fresh `upperSpec`
 */
function scannedUpperSpec(run, objects, ctx) {
  const ids = new Set([run?.id, ...runAliases(run)]);
  const boxes = (objects || []).filter(
    (o) =>
      o?.type === 'upper-cabinet' &&
      ids.has(o.runId) &&
      !isPlainFillUpper(o) &&
      !o.category?.filler &&
      o.label !== 'Over-Fridge Cabinet' &&
      (o.dimensions?.z ?? 0) / IN < BRIDGE_DEPTH_IN &&
      o.center &&
      o.dimensions?.y > 0
  );
  if (!boxes.length) return null;
  const floorY = ctx?.floorY ?? 0;
  const counterTopY = floorY + counterTopOffsetFor(run);
  const bottomOf = (o) => o.center.y - o.dimensions.y / 2;
  const height = median(boxes.map((o) => o.dimensions.y)) / IN;
  const top = median(boxes.map((o) => o.center.y + o.dimensions.y / 2));
  const gap = (median(boxes.map(bottomOf)) - counterTopY) / IN;
  const hung =
    typeof ctx?.ceilingY === 'number' &&
    Math.abs(ctx.ceilingY - top) / IN <= SCANNED_SNAP_IN;
  const spec = {};
  if (hung) spec.anchor = 'ceiling';
  else {
    const g = asChipOrHalfInch(gap, SCANNED_GAP_CHIPS_IN);
    if (Math.abs(g - UPPER_CABINET_GAP_ABOVE_COUNTER) > SAME_LENGTH)
      spec.gap = g;
  }
  const h = asChipOrHalfInch(height, SCANNED_HEIGHT_CHIPS_IN);
  if (Math.abs(h - UPPER_CABINET_DIMENSIONS.height) > SAME_LENGTH)
    spec.height = h;
  return Object.keys(spec).length ? spec : null;
}

module.exports = {
  upperBandFor,
  columnTopY,
  hoodChimneyTopY,
  statedStack,
  stackUnavailableReason,
  roomUpperSpec,
  statedUpperSpec,
  scannedUpperSpec,
};
